import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	TextField,
	Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import PhoneInputField from "src/components/Common/PhoneInputField";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type {
	IUpdateVisitorPayload,
	IVisitorDetails,
} from "../type/newVisitorType";

interface Props {
	open: boolean;
	handleClose: () => void;
	visitorId: number | null;
}

interface FormValues {
	visitor_name: string;
	last_name: string;
	email: string;
	password?: string;
	phone: string;
	organization: string;
	designation: string;
	address: string;
	payment_status: string;
}

const UpdateVisitor: React.FC<Props> = ({ open, handleClose, visitorId }) => {
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const { data: visitor, isLoading: isFetching } =
		useApiQuery<IVisitorDetails>({
			endPoint: `/api/v1/admin/visitor/${visitorId}`,
			queryKeys: ["VisitorDetails", visitorId ?? 0],
			enabled: Boolean(open && visitorId),
		});

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			visitor_name: "",
			last_name: "",
			email: "",
			password: "",
			phone: "",
			organization: "",
			designation: "",
			address: "",
		},
	});

	useEffect(() => {
		if (visitor && open) {
			const ticket = visitor.tickets?.[0];
			reset({
				visitor_name: visitor.visitor_name || "",
				email: visitor.visitor_email || "",
				password: "",
				phone: visitor.visitor_phone || "",
				organization: visitor.visitor_organization || "",
				designation: visitor.visitor_designation || "",
				address: visitor.visitor_address || "",
				payment_status: (ticket?.payment_status || "paid").toLowerCase(),
			});
		}
	}, [visitor, open, reset]);

	const { mutate: updateVisitor, isPending: isUpdating } = useApiMutation<
		IUpdateVisitorPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/v1/admin/visitor/${visitorId}`,
		invalidateQueries: ["Visitors", "VisitorDetails"],
	});

	const onSubmit = (data: FormValues) => {
		const payload: IUpdateVisitorPayload = {
			name: data.visitor_name.trim(),
			email: data.email.trim(),
			...(data.password?.trim() ? { password: data.password.trim() } : {}),
			phone: data.phone ? data.phone.trim() : undefined,
			organization: data.organization.trim() || undefined,
			designation: data.designation.trim() || undefined,
			address: data.address.trim() || undefined,

		};

		updateVisitor(payload, {
			onSuccess: () => {
				reset();
				handleClose();
			},
		});
	};

	const onDialogClose = () => {
		if (isUpdating) return;
		reset();
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="md"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<EditTwoToneIcon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Update Visitor
						{visitor?.visitor_no ? ` (${visitor.visitor_no})` : ""}
					</Typography>
				</Box>
				<IconButton onClick={onDialogClose} size="small" disabled={isUpdating}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2 }}>
				{isFetching ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress />
					</Box>
				) : (
					<Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 1.5,
							}}
						>
							<BadgeIcon color="primary" fontSize="small" />
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="primary.main"
							>
								Personal & Contact Information
							</Typography>
						</Box>

						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<TextField
									fullWidth
									size="small"
									label="Full Name"
									{...register("visitor_name", {
										required: "Full name is required",
									})}
									error={Boolean(errors.visitor_name)}
									helperText={errors.visitor_name?.message}
								/>
							</Grid>

							<Grid item xs={12} sm={4}>
								<TextField
									fullWidth
									size="small"
									type="email"
									label="Email"
									{...register("email", {
										required: "Email is required",
										pattern: {
											value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											message: "Invalid email address",
										},
									})}
									error={Boolean(errors.email)}
									helperText={errors.email?.message}
								/>
							</Grid>



							<Grid item xs={12} sm={4}>
								<PhoneInputField
									name="phone"
									control={control}
									label="Phone Number"
									defaultCountry="bd"
									placeholder="Enter phone number"
									error={Boolean(errors.phone)}
									helperText={errors.phone?.message}
								/>
							</Grid>

							<Grid item xs={12} sm={4}>
								<TextField
									fullWidth
									size="small"
									type={showPassword ? "text" : "password"}
									label="Password"
									placeholder="Leave blank to keep current password"
									{...register("password")}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={() => setShowPassword((prev) => !prev)}
													edge="end"
													size="small"
												>
													{showPassword ? (
														<VisibilityOffIcon fontSize="small" />
													) : (
														<VisibilityIcon fontSize="small" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									error={Boolean(errors.password)}
								/>
							</Grid>


						</Grid>
					</Box>
				)}
			</DialogContent>

			<Divider />

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button
					onClick={onDialogClose}
					variant="outlined"
					disabled={isUpdating}
					sx={{
						borderColor: "grey.400",
						color: "grey.700",
						"&:hover": { backgroundColor: "grey.100" },
					}}
				>
					Cancel
				</Button>
				<LoadingButton
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					loading={isUpdating}
					disabled={isFetching}
				>
					<span>Save Changes</span>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default UpdateVisitor;
